import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Upload, Camera, Loader2, CheckCircle, XCircle, Clock, ArrowLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useEffect } from 'react';

const STATUS_CONFIG = {
  unverified: { icon: AlertTriangle, color: 'text-[#78716C]', bg: 'bg-[#F9F5F0]', label: 'Not Verified' },
  pending: { icon: Clock, color: 'text-[#F2A900]', bg: 'bg-[#F2A900]/10', label: 'Under Review' },
  verified: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Verified' },
  rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Rejected' },
};

export default function KYCPage() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [kycStatus, setKycStatus] = useState('unverified');
  const [submissions, setSubmissions] = useState([]);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await api.get('/kyc/status');
        setKycStatus(res.data.kyc_status);
        setSubmissions(res.data.submissions || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStatus();
  }, []);

  const handleFileSelect = (file, side) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      if (side === 'front') { setFrontPreview(e.target.result); setFrontFile(file); }
      else { setBackPreview(e.target.result); setBackFile(file); }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!frontFile || !backFile) {
      toast.error('Please upload both front and back of your Aadhaar card');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('front', frontFile);
      formData.append('back', backFile);
      await api.post('/kyc/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('KYC documents submitted! We will review within 24-48 hours.');
      setKycStatus('pending');
      setFrontFile(null);
      setBackFile(null);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setUploading(false);
    }
  };

  const statusConfig = STATUS_CONFIG[kycStatus] || STATUS_CONFIG.unverified;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="kyc-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold text-[#2D2424]">KYC Verification</h1>
      </div>

      {/* Status Card */}
      <Card className={`p-5 rounded-xl border border-[#E7E5E4] mb-6 ${statusConfig.bg}`}>
        <div className="flex items-center gap-3">
          <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
          <div>
            <div className={`font-semibold ${statusConfig.color}`}>{statusConfig.label}</div>
            <p className="text-sm text-[#78716C]">
              {kycStatus === 'unverified' && 'Submit your Aadhaar card to get verified'}
              {kycStatus === 'pending' && 'Your documents are being reviewed. This usually takes 24-48 hours.'}
              {kycStatus === 'verified' && 'Your identity is verified. You have a green verified badge.'}
              {kycStatus === 'rejected' && 'Your submission was rejected. Please resubmit with clear documents.'}
            </p>
          </div>
        </div>
      </Card>

      {/* Submission History */}
      {submissions.length > 0 && (
        <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white mb-6">
          <h3 className="text-sm font-semibold text-[#2D2424] mb-3">Submission History</h3>
          <div className="space-y-2">
            {submissions.map((sub) => {
              const sc = STATUS_CONFIG[sub.status] || STATUS_CONFIG.unverified;
              const SI = sc.icon;
              return (
                <div key={sub.id} className="flex items-center justify-between py-2 border-b border-[#E7E5E4]/50 last:border-0">
                  <div className="flex items-center gap-2">
                    <SI className={`w-4 h-4 ${sc.color}`} />
                    <span className="text-sm text-[#2D2424]">{new Date(sub.submitted_at).toLocaleDateString()}</span>
                  </div>
                  <Badge className={sc.bg + ' ' + sc.color}>{sub.status}</Badge>
                  {sub.rejection_reason && <span className="text-xs text-red-600 ml-2">{sub.rejection_reason}</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Upload Form (show only if not pending or verified) */}
      {(kycStatus === 'unverified' || kycStatus === 'rejected') && (
        <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
          <h3 className="text-lg font-semibold text-[#2D2424] mb-2">Upload Aadhaar Card</h3>
          <p className="text-sm text-[#78716C] mb-6">Upload clear photos of the front and back of your Aadhaar card</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Front */}
            <div>
              <Label className="text-sm font-medium text-[#2D2424] mb-2 block">Front Side</Label>
              <button onClick={() => frontRef.current?.click()}
                className="w-full aspect-[3/2] rounded-xl border-2 border-dashed border-[#E7E5E4] hover:border-[#D9381E] flex flex-col items-center justify-center gap-2 transition-colors overflow-hidden"
                data-testid="kyc-front-upload">
                {frontPreview ? (
                  <img src={frontPreview} alt="Front" className="w-full h-full object-cover" />
                ) : (
                  <><Camera className="w-8 h-8 text-[#78716C]" /><span className="text-xs text-[#78716C]">Aadhaar Front</span></>
                )}
              </button>
              <input ref={frontRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0], 'front')} />
            </div>
            {/* Back */}
            <div>
              <Label className="text-sm font-medium text-[#2D2424] mb-2 block">Back Side</Label>
              <button onClick={() => backRef.current?.click()}
                className="w-full aspect-[3/2] rounded-xl border-2 border-dashed border-[#E7E5E4] hover:border-[#D9381E] flex flex-col items-center justify-center gap-2 transition-colors overflow-hidden"
                data-testid="kyc-back-upload">
                {backPreview ? (
                  <img src={backPreview} alt="Back" className="w-full h-full object-cover" />
                ) : (
                  <><Camera className="w-8 h-8 text-[#78716C]" /><span className="text-xs text-[#78716C]">Aadhaar Back</span></>
                )}
              </button>
              <input ref={backRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0], 'back')} />
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={uploading || !frontFile || !backFile}
            className="w-full bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full py-5 font-semibold disabled:opacity-50"
            data-testid="kyc-submit-btn">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : <><Upload className="w-4 h-4 mr-2" /> Submit for Verification</>}
          </Button>

          <p className="mt-4 text-xs text-[#78716C] text-center">
            Your Aadhaar number will NOT be stored. Only verification status is saved.
          </p>
        </Card>
      )}
    </div>
  );
}

function Label({ children, className }) {
  return <label className={className}>{children}</label>;
}
